import { useEffect, useState, useCallback } from "react";
import {
  Radio, Server, Globe, Shield, Mail, MessageCircle, Music,
  BarChart3, Code, Share2, CreditCard, HardDrive,
  RefreshCw, ExternalLink, ChevronDown, ChevronUp,
  CheckCircle2, Clock, AlertTriangle, XCircle, MinusCircle,
  Activity
} from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const ICON_MAP = {
  radio: Radio, server: Server, globe: Globe, shield: Shield,
  mail: Mail, "message-circle": MessageCircle, music: Music,
  "bar-chart": BarChart3, code: Code, "share-2": Share2,
  "credit-card": CreditCard, "hard-drive": HardDrive,
};

const STATUS_CONFIG = {
  connected: { label: "Connected", color: "#10b981", bg: "rgba(16,185,129,0.12)", icon: CheckCircle2 },
  active:    { label: "Active",    color: "#10b981", bg: "rgba(16,185,129,0.12)", icon: CheckCircle2 },
  pending:   { label: "Pending",   color: "#f59e0b", bg: "rgba(245,158,11,0.12)", icon: Clock },
  error:     { label: "Error",     color: "#ef4444", bg: "rgba(239,68,68,0.12)",  icon: AlertTriangle },
  not_configured: { label: "Not Configured", color: "#6b7280", bg: "rgba(107,114,128,0.10)", icon: MinusCircle },
};

const CATEGORY_LABELS = {
  core: "Core Services",
  infrastructure: "Infrastructure",
  features: "Station Features",
  growth: "Growth & Engagement",
};

const TAB_ACTIONS = {
  stream: "station",
  azuracast: "station",
  domain: "domains",
  ssl: "domains",
  email: "email",
  chat: "pagebuilder",
  requests: "pagebuilder",
  analytics: "analytics",
  embeds: "embed",
  social: "pagebuilder",
  payments: "billing",
  storage: "library",
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_configured;
  const Icon = cfg.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.color }}
      data-testid={`status-badge-${status}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
};

const ServiceCard = ({ service, onNavigate }) => {
  const [expanded, setExpanded] = useState(false);
  const IconComp = ICON_MAP[service.icon] || Radio;
  const statusCfg = STATUS_CONFIG[service.status] || STATUS_CONFIG.not_configured;
  const targetTab = TAB_ACTIONS[service.id];

  return (
    <div
      className="rounded-xl border transition-all duration-200 hover:border-white/20"
      style={{ background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}
      data-testid={`health-card-${service.id}`}
    >
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: statusCfg.bg }}
          >
            <IconComp className="w-5 h-5" style={{ color: statusCfg.color }} />
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium">{service.name}</p>
            <p className="text-white/40 text-xs truncate max-w-[260px]">{service.detail}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <StatusBadge status={service.status} />
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/5 transition"
            data-testid={`health-expand-${service.id}`}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 pt-0 border-t border-white/5">
          <div className="pt-3 flex items-center gap-2 flex-wrap">
            {targetTab && (
              <button
                onClick={() => onNavigate(targetTab)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium bg-white/5 hover:bg-white/10 text-white/70 hover:text-white transition"
                data-testid={`health-configure-${service.id}`}
              >
                <ExternalLink className="w-3 h-3" />
                Configure
              </button>
            )}
            {(service.status === "connected" || service.status === "active") && service.detail && (
              <span className="text-xs text-white/30 ml-auto">Last checked just now</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const StationHealthTab = ({ currentUser, authToken, onTabChange }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealth = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const t = localStorage.getItem("token") || authToken;
      const res = await fetch(`${BACKEND_URL}/api/station-health`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error("Station health fetch failed:", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [authToken]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  const navigate = (tab) => { if (onTabChange) onTabChange(tab); };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="health-loading">
        <Activity className="w-8 h-8 text-purple-400 animate-pulse" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20 text-white/40" data-testid="health-error">
        <XCircle className="w-10 h-10 mx-auto mb-3 text-red-400/50" />
        <p>Could not load station health. Try refreshing.</p>
        <button onClick={() => fetchHealth()} className="mt-4 px-4 py-2 bg-white/10 rounded-lg text-white/70 hover:text-white text-sm transition">Retry</button>
      </div>
    );
  }

  const { services, summary, total } = data;
  const healthy = (summary.connected || 0) + (summary.active || 0);
  const healthPct = total > 0 ? Math.round((healthy / total) * 100) : 0;

  // Group by category
  const grouped = {};
  for (const s of services) {
    const cat = s.category || "other";
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(s);
  }

  const ringColor = healthPct >= 80 ? "#10b981" : healthPct >= 50 ? "#f59e0b" : "#ef4444";
  const circumference = 2 * Math.PI * 40;
  const strokeDash = (healthPct / 100) * circumference;

  return (
    <div className="space-y-6" data-testid="station-health-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white">Station Health</h2>
          <p className="text-white/40 text-sm">Monitor all connected services and integrations</p>
        </div>
        <button
          onClick={() => fetchHealth(true)}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 hover:bg-white/10 text-white/70 hover:text-white text-sm transition disabled:opacity-50"
          data-testid="health-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Checking..." : "Refresh"}
        </button>
      </div>

      {/* Summary Ring + Counters */}
      <div className="grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-center rounded-2xl p-6" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
        {/* Ring */}
        <div className="flex items-center justify-center">
          <div className="relative w-28 h-28">
            <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
              <circle cx="50" cy="50" r="40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="8" />
              <circle
                cx="50" cy="50" r="40" fill="none"
                stroke={ringColor} strokeWidth="8"
                strokeLinecap="round"
                strokeDasharray={`${strokeDash} ${circumference}`}
                style={{ transition: "stroke-dasharray 0.6s ease" }}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-2xl font-bold text-white">{healthPct}%</span>
              <span className="text-[10px] text-white/40 uppercase tracking-wider">Health</span>
            </div>
          </div>
        </div>

        {/* Counters */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { key: "connected", label: "Connected", color: "#10b981" },
            { key: "active", label: "Active", color: "#10b981" },
            { key: "pending", label: "Pending", color: "#f59e0b" },
            { key: "error", label: "Error", color: "#ef4444" },
            { key: "not_configured", label: "Not Set Up", color: "#6b7280" },
          ].map(({ key, label, color }) => (
            <div key={key} className="rounded-xl p-3 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
              <p className="text-xl font-bold" style={{ color }}>{summary[key] || 0}</p>
              <p className="text-[11px] text-white/40">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Grouped Service Cards */}
      {Object.entries(CATEGORY_LABELS).map(([cat, label]) => {
        const items = grouped[cat];
        if (!items || items.length === 0) return null;
        return (
          <div key={cat}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-white/25 mb-3">{label}</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {items.map(s => (
                <ServiceCard key={s.id} service={s} onNavigate={navigate} />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
